import type { LoadMode } from './config';
import { loadFrameBatch, parseFrameImageId } from './framebatch';

/**
 * Whole-stack download after the first image is up. Both modes fetch every image of a displayed
 * series; they differ in order, pacing and failure handling.
 *
 * parity (IMEXHS v7.0.1): index 0 upward regardless of where the user is, 16 requests up front,
 * then refills of 8 whenever only 8 remain in flight.
 * fast: nearest-first outward from the slice on screen, re-centred when the user scrolls, a steady
 * window of requests, and at most MAX_ATTEMPTS tries per image with a growing pause.
 *
 * IMEXHS retries a failed image immediately and forever; both modes here cap retries, since an
 * unbounded hot loop against a failing PACS is a defect, not behaviour worth matching.
 */

const PARITY_FIRST_BURST = 16;
const PARITY_REFILL = 8;
const FAST_IN_FLIGHT = 12;
/** Frames of one multi-frame instance fetched per WADO-RS request in fast mode. */
const FAST_FRAME_BATCH = 16;
const MAX_ATTEMPTS = 3;
const RETRY_BASE_MS = 500;

const PREFETCH_OPTIONS = { requestType: 'prefetch', priority: 10 } as const;

export interface StackPrefetcher {
  /** Tell the prefetcher which slice is on screen (fast mode re-centres on it). */
  focus(index: number): void;
  cancel(): void;
  readonly done: Promise<void>;
}

export function startStackPrefetch(
  mode: LoadMode,
  imageIds: string[],
  startIndex: number,
  onProgress: (loaded: number, total: number) => void,
  headers: () => Record<string, string> = () => ({}),
): StackPrefetcher {
  return mode === 'parity'
    ? parityPrefetch(imageIds, onProgress)
    : fastPrefetch(imageIds, startIndex, onProgress, headers);
}

function loadWithRetry(imageId: string, isCancelled: () => boolean): Promise<void> {
  const attempt = async (n: number): Promise<void> => {
    try {
      await window.cornerstone.loadAndCacheImage(imageId, PREFETCH_OPTIONS);
    } catch (error) {
      if (isCancelled() || n + 1 >= MAX_ATTEMPTS) {
        console.warn('[viewer] image failed after retries', imageId.slice(-60), error);
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, RETRY_BASE_MS * 2 ** n));
      await attempt(n + 1);
    }
  };
  return attempt(0);
}

function parityPrefetch(imageIds: string[], onProgress: (loaded: number, total: number) => void): StackPrefetcher {
  let cancelled = false;
  let next = 0;
  let inFlight = 0;
  let loaded = 0;
  let resolveDone: () => void = () => undefined;
  const done = new Promise<void>((resolve) => { resolveDone = resolve; });
  const isCancelled = () => cancelled;

  const launch = (count: number) => {
    for (let i = 0; i < count && next < imageIds.length && !cancelled; i++) {
      const imageId = imageIds[next++];
      inFlight++;
      void loadWithRetry(imageId, isCancelled).then(() => {
        inFlight--;
        loaded++;
        onProgress(loaded, imageIds.length);
        if (loaded === imageIds.length || cancelled) resolveDone();
        else if (inFlight <= PARITY_REFILL) launch(PARITY_REFILL);
      });
    }
  };
  launch(PARITY_FIRST_BURST);
  if (imageIds.length === 0) resolveDone();
  return { focus: () => undefined, cancel: () => { cancelled = true; resolveDone(); }, done };
}

function fastPrefetch(
  imageIds: string[],
  startIndex: number,
  onProgress: (loaded: number, total: number) => void,
  headers: () => Record<string, string>,
): StackPrefetcher {
  let cancelled = false;
  let center = startIndex;
  let inFlight = 0;
  let loaded = 0;
  const pending = new Set(imageIds.keys());
  const abort = new AbortController();
  let resolveDone: () => void = () => undefined;
  const done = new Promise<void>((resolve) => { resolveDone = resolve; });
  const isCancelled = () => cancelled;
  const frames = imageIds.map(parseFrameImageId);

  const nearest = (): number | undefined => {
    for (let d = 0; d < imageIds.length; d++) {
      if (pending.has(center + d)) return center + d;
      if (pending.has(center - d)) return center - d;
    }
    return undefined;
  };
  /** Pending indices around `index` that are frames of the same instance, nearest first. */
  const batchAround = (index: number): number[] => {
    const base = frames[index]?.base;
    if (!base) return [index];
    const batch = [index];
    for (let d = 1; batch.length < FAST_FRAME_BATCH && d < imageIds.length; d++) {
      for (const candidate of [index + d, index - d]) {
        if (batch.length < FAST_FRAME_BATCH && pending.has(candidate) && frames[candidate]?.base === base) batch.push(candidate);
      }
    }
    return batch;
  };
  const finish = (count: number) => {
    inFlight--;
    loaded += count;
    onProgress(loaded, imageIds.length);
    if (loaded >= imageIds.length || cancelled) resolveDone();
    else pump();
  };
  const loadBatch = async (indices: number[]): Promise<void> => {
    const base = frames[indices[0]]?.base as string;
    try {
      await loadFrameBatch(base, indices.map((i) => frames[i]?.frame as number), headers(), abort.signal);
    } catch (error) {
      if (cancelled) return;
      console.warn('[viewer] frame batch failed, loading frames one by one', error);
      await Promise.all(indices.map((i) => loadWithRetry(imageIds[i], isCancelled)));
    }
  };
  const pump = () => {
    while (!cancelled && inFlight < FAST_IN_FLIGHT) {
      const index = nearest();
      if (index === undefined) return;
      const batch = batchAround(index);
      batch.forEach((i) => pending.delete(i));
      inFlight++;
      const work = batch.length > 1 ? loadBatch(batch) : loadWithRetry(imageIds[index], isCancelled);
      void work.then(() => finish(batch.length));
    }
  };
  pending.delete(startIndex);
  loaded = 1;
  onProgress(loaded, imageIds.length);
  if (imageIds.length <= 1) resolveDone();
  pump();
  return {
    focus: (index) => { center = index; },
    cancel: () => { cancelled = true; abort.abort(); resolveDone(); },
    done,
  };
}
